import { z } from 'zod';
import { isSelectableAvatar } from '@/const';
import isValidCPF from '@/utils/isValidCPF';

const createPhoneValidation = (isPhoneOptional: boolean) => {
  if (isPhoneOptional) {
    return z.string().optional();
  }

  return z
    .string({ error: 'Esse campo é obrigatório' })
    .min(1, 'Esse campo é obrigatório')
    .refine(
      (value) => {
        const cleanPhone = value.replace(/\D/g, '');
        return cleanPhone.length === 11;
      },
      { message: 'Telefone deve ter 11 dígitos' },
    )
    .refine(
      (value) => {
        const cleanPhone = value.replace(/\D/g, '');
        return /^[1-9][1-9]9\d{8}$/.test(cleanPhone);
      },
      { message: 'Use o formato (99) 99999-9999' },
    );
};

// `isWebView` is accepted for call-site compatibility but no longer affects
// validation: phone-optionality is decided by the auth method (`skipPassword`
// = social) so a spoofable client UA flag can't make the phone optional
// (issue #5957). The authoritative verified-channel gate is server-side in
// register().
const RegisterFormSchema = (_isWebView: boolean, skipPassword = false) =>
  z
    .object({
      name: z
        .string({ error: 'Esse campo é obrigatório' })
        .min(1, 'Esse campo é obrigatório')
        .min(3, 'Informe o nome completo'),
      email: z
        .string({ error: 'Esse campo é obrigatório' })
        .min(1, 'Esse campo é obrigatório')
        .trim()
        .email('Informe um e-mail válido')
        .transform((val) => val.toLowerCase()),
      dateOfBirth: z
        .string({ error: 'Esse campo é obrigatório' })
        .min(1, 'Esse campo é obrigatório')
        .min(10, 'Informe uma data válida')
        .refine((date) => {
          const [day, month, year] = date.split('/').map(Number);

          if (!day || !month || !year) return false;
          if (day < 1 || day > 31) return false;
          if (month < 1 || month > 12) return false;

          const dateObj = new Date(year, month - 1, day);
          return (
            dateObj.getDate() === day &&
            dateObj.getMonth() === month - 1 &&
            dateObj.getFullYear() === year
          );
        }, 'Data inválida')
        .refine((date) => {
          const [, , year] = date.split('/').map(Number);
          const currentYear = new Date().getFullYear();
          return year <= currentYear;
        }, 'Ano não pode ser maior que o atual')
        .refine((date) => {
          const [day, month, year] = date.split('/').map(Number);
          const birthDate = new Date(year, month - 1, day);
          const today = new Date();

          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();

          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            age--;
          }

          return age >= 18;
        }, 'Você deve ter pelo menos 18 anos'),
      dateOfBirthTerms: z.boolean().refine((val) => val, {
        message: 'É preciso concordar para continuar',
      }),
      avatarImage: z
        .string()
        .refine((value): boolean => isSelectableAvatar(value), {
          message: 'Avatar inválido',
        }),
      // Password fields are optional for social auth
      password: skipPassword
        ? z.string().optional()
        : z
            .string({ error: 'Esse campo é obrigatório' })
            .min(1, 'Esse campo é obrigatório')
            .min(8, 'A senha deve ter no mínimo 8 caracteres'),
      document: z
        .string({ error: 'Esse campo é obrigatório' })
        .min(1, 'Esse campo é obrigatório')
        .refine((value) => isValidCPF(value), {
          message: 'CPF inválido',
        }),
      // Phone is optional only for social auth (the OAuth-verified email is the
      // channel); every credentials signup must submit one. Never keyed on the
      // client `isWebView` UA flag, which is spoofable and previously allowed a
      // phone-less account to be created (issue #5957).
      phoneNumber: createPhoneValidation(skipPassword),
      passwordConfirmation: skipPassword
        ? z.string().optional()
        : z
            .string({ error: 'Esse campo é obrigatório' })
            .min(1, 'Esse campo é obrigatório')
            .min(8, 'A senha deve ter no mínimo 8 caracteres'),
    })
    .superRefine((data, ctx) => {
      const trimmedName = data.name.trim().replace(/\s+/g, ' ');

      if (trimmedName.length >= 3 && !/^[a-zA-ZÀ-ÿ\s]+$/.test(trimmedName)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Nome deve conter apenas letras e espaços',
          path: ['name'],
        });
      } else if (trimmedName.length >= 3) {
        const names = trimmedName.split(' ');

        if (names.length < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Informe seu nome e sobrenome',
            path: ['name'],
          });
        } else {
          const firstName = names[0].toLowerCase();
          if (names.slice(1).some((n) => n.toLowerCase() === firstName)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'O primeiro nome não pode ser igual ao sobrenome',
              path: ['name'],
            });
          }
        }
      }

      // Skip password validation for social auth
      if (skipPassword) return;
      if (data.password !== data.passwordConfirmation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'As senhas não coincidem',
          path: ['passwordConfirmation'],
        });
      }
    });

export type RegisterFormSchemaType = ReturnType<typeof RegisterFormSchema>;

export default RegisterFormSchema;
